/**
 * A sessao vista pelo app — ADR-0009, `CONTRACTS §5`.
 *
 * ## O que NAO existe aqui, e por que
 *
 * Nao ha token. Nao ha `localStorage.setItem('token', ...)`. Nao ha `Authorization`. A sessao e
 * **opaca, em cookie `__Host-session` `HttpOnly`** — o JavaScript nao a le e nao pode le-la, por
 * desenho. O que este contexto guarda e uma unica informacao derivada: **se ha sessao**, que o
 * app precisa saber para escolher a politica de cache do catalogo (`catalogCachePolicy`, que e
 * `no-store` quando ha sessao por causa do preco de clube) e para decidir entre "Entrar" e
 * "Minha conta".
 *
 * Como saber "se ha sessao" sem ler o cookie? Perguntando ao servidor: uma chamada autenticada
 * que responde 200 significa sessao; `401` significa que nao ha. O estado aqui e **espelho**,
 * nunca fonte — e a diferenca importa: uma tela que confiasse neste booleano para liberar algo
 * estaria fazendo autorizacao no cliente, que o §33 proibe. Ele so decide **o que mostrar**.
 *
 * ## A filial saiu daqui
 *
 * `storeId` morava neste contexto e foi para `loja/contexto.tsx` (`useMinhaLoja`). O motivo nao
 * e arrumacao: a filial escolhida **sobrevive ao logout** por decisao de documento (`PWA §7.2`,
 * o store `kv` e o unico com `limpaNoLogout: false` — *"preferencia do aparelho, nao do
 * titular"*). Guardar num provedor chamado "sessao" algo que a sessao nao apaga convida ao bug
 * simetrico: alguem limpa o contexto no logout e a pessoa perde a filial que escolheu.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type EstadoDaSessao = 'DESCONHECIDA' | 'ANONIMA' | 'AUTENTICADA';

export interface Sessao {
  readonly estado: EstadoDaSessao;
  /** Atalho de leitura. `false` enquanto `DESCONHECIDA`: o padrao e o mais restritivo. */
  readonly temSessao: boolean;
  readonly definirEstado: (estado: EstadoDaSessao) => void;
}

const ContextoDeSessao = createContext<Sessao | null>(null);

export interface ProvedorDeSessaoProps {
  readonly children: ReactNode;
  readonly estadoInicial?: EstadoDaSessao;
}

export function ProvedorDeSessao({
  children,
  estadoInicial = 'DESCONHECIDA',
}: ProvedorDeSessaoProps): ReactNode {
  const [estado, setEstado] = useState<EstadoDaSessao>(estadoInicial);

  const definirEstado = useCallback((novo: EstadoDaSessao) => {
    setEstado(novo);
  }, []);

  const valor = useMemo<Sessao>(
    () => ({
      estado,
      temSessao: estado === 'AUTENTICADA',
      definirEstado,
    }),
    [estado, definirEstado],
  );

  return <ContextoDeSessao.Provider value={valor}>{children}</ContextoDeSessao.Provider>;
}

export function useSessao(): Sessao {
  const valor = useContext(ContextoDeSessao);
  if (valor === null) {
    throw new Error('Use <ProvedorDeSessao> acima do componente que consome a sessao.');
  }
  return valor;
}
